import React, { useState } from 'react';
import { Product, Article, ProductTrustBadge, ProductItineraryItem, ProductFaqItem } from '../../types.ts';
import ProductMediaUploader from './ProductMediaUploader.tsx';
import ProductRelatedArticlesManager from './ProductRelatedArticlesManager.tsx';
import {
  Tag,
  EyeOff,
  MessageSquare,
  Plus,
  Trash2,
  ShieldCheck,
  CheckCircle2,
  Clock,
  FileText,
  HelpCircle,
  Briefcase,
  Layers,
  BookOpen,
  Info,
  Phone,
  Share2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface ProductSinglePageEditorProps {
  product: Partial<Product>;
  articles: Article[];
  onChange: (updatedProduct: Partial<Product>) => void;
  primaryColor: string;
}

export default function ProductSinglePageEditor({
  product,
  articles,
  onChange,
  primaryColor,
}: ProductSinglePageEditorProps) {
  const [activeTab, setActiveTab] = useState<'basic' | 'price' | 'features' | 'guarantee' | 'itinerary' | 'faqs' | 'proposal' | 'related'>('basic');

  const updateField = (key: keyof Product, value: any) => {
    onChange({ ...product, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Tab Selector Bar */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-slate-200 scrollbar-thin">
        <button
          type="button"
          onClick={() => setActiveTab('basic')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'basic'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Info className="w-3.5 h-3.5" />
          <span>Informasi & Media</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('price')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'price'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Tag className="w-3.5 h-3.5" />
          <span>Harga & Tombol WA/Kontak</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('features')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'features'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>Fasilitas, Inclusions & Exclusions</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('guarantee')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'guarantee'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Jaminan Layanan & Deskripsi</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('itinerary')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'itinerary'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          <span>Rundown / Itinerary</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('faqs')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'faqs'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
          <span>Syarat & FAQ</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('proposal')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'proposal'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Briefcase className="w-3.5 h-3.5" />
          <span>Box Proposal Corporate</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('related')}
          className={`px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
            activeTab === 'related'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          <span>Artikel & Paket Terkait</span>
        </button>
      </div>

      {/* TAB 1: INFORMASI DASAR & MEDIA */}
      {activeTab === 'basic' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Judul Layanan / Paket *</label>
              <input
                type="text"
                required
                value={product.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold"
                placeholder="Contoh: Paket Outbound Corporate Lembang Bandung"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Kategori Layanan *</label>
              <input
                type="text"
                required
                value={product.category || ''}
                onChange={(e) => updateField('category', e.target.value)}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                placeholder="Outbound / Gathering / Rafting / Offroad / Tour"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Badge Highlight Gambar (Opsional)</label>
              <input
                type="text"
                value={product.badge || ''}
                onChange={(e) => updateField('badge', e.target.value)}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-semibold text-amber-700"
                placeholder="BEST SELLER / REKOMENDASI / DISKON 20%"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Status Ketersediaan / Stock</label>
              <input
                type="text"
                value={product.availabilityStatus ?? 'Siap Dipesan / Tersedia'}
                onChange={(e) => updateField('availabilityStatus', e.target.value)}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl text-emerald-800 font-bold"
                placeholder="Siap Dipesan / Tersedia (Kosongkan jika ingin disembunyikan)"
              />
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-700">URL Slug Halaman Single Produk/Layanan:</label>
              <button
                type="button"
                onClick={() => {
                  const autoSlug = (product.title || '')
                    .toLowerCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/[\s_-]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  updateField('slug', autoSlug);
                }}
                className="text-[10px] font-bold text-emerald-700 hover:underline cursor-pointer"
              >
                Sinkronkan dari Judul
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-slate-400 select-none">/produk/</span>
              <input
                type="text"
                value={product.slug || ''}
                onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="paket-outbound-lembang-bandung"
                className="flex-1 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg font-mono text-slate-800 font-bold"
              />
            </div>
          </div>

          {/* Media Uploader Component */}
          <ProductMediaUploader
            coverImage={product.image || ''}
            galleryImages={product.gallery || (product.image ? [product.image] : [])}
            videoUrl={product.videoUrl || ''}
            videoType={product.videoType || 'file'}
            onUpdateCover={(cover) => updateField('image', cover)}
            onUpdateGallery={(gallery) => updateField('gallery', gallery)}
            onUpdateVideo={(vUrl, vType) => {
              updateField('videoUrl', vUrl);
              updateField('videoType', vType);
            }}
            primaryColor={primaryColor}
          />
        </div>
      )}

      {/* TAB 2: HARGA & TOMBOL KONTAK */}
      {activeTab === 'price' && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-emerald-600" />
                <span>Pengaturan Harga & Nominal:</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs hover:bg-slate-50 transition-colors">
                <input
                  type="checkbox"
                  checked={Boolean(product.hidePrice)}
                  onChange={(e) => updateField('hidePrice', e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded-md border-slate-300 focus:ring-emerald-500"
                />
                <span className="text-xs font-bold text-slate-700">
                  Sembunyikan Harga Nominal (Opsi Hubungi Kami)
                </span>
              </label>
            </div>

            {product.hidePrice ? (
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 space-y-2">
                <div className="flex items-center gap-2 text-amber-900 font-bold text-xs">
                  <EyeOff className="w-4 h-4 text-amber-700" />
                  <span>Harga nominal (Rp) disembunyikan dari halaman publik</span>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Teks Label Pengganti Harga:</label>
                  <input
                    type="text"
                    placeholder="Contoh: Hubungi Kami / Hubungi Admin via WA / Sesuai Kebutuhan"
                    value={product.priceCustomLabel || ''}
                    onChange={(e) => updateField('priceCustomLabel', e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-slate-800"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Harga Diskon / Promo (Rp) *</label>
                  <input
                    type="number"
                    required
                    value={product.price ?? ''}
                    onChange={(e) => updateField('price', Number(e.target.value))}
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold text-emerald-800"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Harga Asli / Coret (Rp)</label>
                  <input
                    type="number"
                    placeholder="450000"
                    value={product.originalPrice ?? ''}
                    onChange={(e) => updateField('originalPrice', Number(e.target.value))}
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Harga</label>
                <input
                  type="text"
                  value={product.priceLabel || ''}
                  onChange={(e) => updateField('priceLabel', e.target.value)}
                  placeholder={product.hidePrice ? 'Estimasi Biaya & Penawaran Paket:' : 'Biaya Investasi / Harga Paket:'}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Catatan Keterangan Harga (Bawah Box)</label>
                <input
                  type="text"
                  value={product.priceNote || ''}
                  onChange={(e) => updateField('priceNote', e.target.value)}
                  placeholder="*Harga sudah mencakup perlengkapan standar & pendampingan..."
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* WhatsApp & Button Settings */}
          <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-200/80 space-y-3">
            <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-emerald-600" />
              <span>Pengaturan Tombol CTA WhatsApp & Kontak</span>
            </h4>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Teks Tombol Utama Pemesanan WA:</label>
              <input
                type="text"
                value={product.orderButtonText || ''}
                onChange={(e) => updateField('orderButtonText', e.target.value)}
                placeholder="Pesan & Konsultasi via WhatsApp"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Pesan Otomatis Teks WhatsApp:</label>
              <textarea
                rows={2}
                value={product.whatsappMessage || ''}
                onChange={(e) => updateField('whatsappMessage', e.target.value)}
                placeholder={`Halo Admin, saya tertarik untuk memesan "${product.title || 'paket ini'}". Mohon info ketersediaan...`}
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-emerald-200/60">
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={product.showPhoneButton !== false}
                    onChange={(e) => updateField('showPhoneButton', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded-md"
                  />
                  <span className="text-xs font-bold text-slate-800">Tampilkan Tombol Telepon Kantor</span>
                </label>
                {product.showPhoneButton !== false && (
                  <input
                    type="text"
                    value={product.phoneButtonText || ''}
                    onChange={(e) => updateField('phoneButtonText', e.target.value)}
                    placeholder="Telepon Kantor"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                )}
              </div>

              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={product.showShareButton !== false}
                    onChange={(e) => updateField('showShareButton', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded-md"
                  />
                  <span className="text-xs font-bold text-slate-800">Tampilkan Tombol Bagikan Link</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: FASILITAS, INCLUSIONS & EXCLUSIONS */}
      {activeTab === 'features' && (
        <div className="space-y-4">
          <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-emerald-600" />
                <span>Fasilitas & Poin Keunggulan Utama</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={product.showFeatures !== false}
                  onChange={(e) => updateField('showFeatures', e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Seksi Ini</span>
              </label>
            </div>

            {product.showFeatures !== false && (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Fasilitas:</label>
                  <input
                    type="text"
                    value={product.featuresTitle || ''}
                    onChange={(e) => updateField('featuresTitle', e.target.value)}
                    placeholder="Fasilitas & Keunggulan Paket Ini:"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Daftar Fasilitas (Satu Poin per Baris):
                  </label>
                  <textarea
                    rows={4}
                    value={
                      Array.isArray(product.features)
                        ? product.features.join('\n')
                        : (product.features as any) || ''
                    }
                    onChange={(e) => updateField('features', e.target.value.split('\n'))}
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono"
                    placeholder="Fasilitator Profesional&#10;Peralatan Standar Internasional&#10;Makan Siang & Snack Box"
                  />
                </div>
              </>
            )}
          </div>

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Hal Termasuk & Tidak Termasuk (Inclusions / Exclusions)</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(product.showInclusionsExclusions)}
                  onChange={(e) => updateField('showInclusionsExclusions', e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Seksi Ini</span>
              </label>
            </div>

            {product.showInclusionsExclusions && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-2">
                  <label className="text-xs font-bold text-emerald-900 block">Judul Seksi Termasuk (Inclusions)</label>
                  <input
                    type="text"
                    value={product.inclusionsTitle || ''}
                    onChange={(e) => updateField('inclusionsTitle', e.target.value)}
                    placeholder="Termasuk Dalam Paket (Inclusions)"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <label className="text-xs font-bold text-emerald-900 block">Poin-poin Termasuk (1 per baris):</label>
                  <textarea
                    rows={4}
                    value={(product.inclusions || []).join('\n')}
                    onChange={(e) => updateField('inclusions', e.target.value.split('\n'))}
                    placeholder="Tiket Masuk Lokasi&#10;Makan 3x Sehari&#10;Dokumentasi Foto"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg font-mono"
                  />
                </div>

                <div className="p-3 bg-rose-50/50 rounded-xl border border-rose-200 space-y-2">
                  <label className="text-xs font-bold text-rose-900 block">Judul Seksi Tidak Termasuk (Exclusions)</label>
                  <input
                    type="text"
                    value={product.exclusionsTitle || ''}
                    onChange={(e) => updateField('exclusionsTitle', e.target.value)}
                    placeholder="Tidak Termasuk Dalam Paket (Exclusions)"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <label className="text-xs font-bold text-rose-900 block">Poin-poin Tidak Termasuk (1 per baris):</label>
                  <textarea
                    rows={4}
                    value={(product.exclusions || []).join('\n')}
                    onChange={(e) => updateField('exclusions', e.target.value.split('\n'))}
                    placeholder="Pengeluaran Pribadi&#10;Tips Guide & Driver&#10;Asuransi Tambahan"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg font-mono"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: JAMINAN LAYANAN & DESKRIPSI */}
      {activeTab === 'guarantee' && (
        <div className="space-y-4">
          <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Box Jaminan Layanan / Legalitas (Trust Badges)</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={product.showTrustBadges !== false}
                  onChange={(e) => updateField('showTrustBadges', e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Box Jaminan</span>
              </label>
            </div>

            {product.showTrustBadges !== false && (
              <div className="space-y-2">
                {(product.trustBadges || [
                  { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                  { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' },
                ]).map((badge, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl border border-slate-200">
                    <input
                      type="text"
                      value={badge.title}
                      onChange={(e) => {
                        const newBadges = [...(product.trustBadges || [
                          { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                          { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' },
                        ])];
                        newBadges[idx].title = e.target.value;
                        updateField('trustBadges', newBadges);
                      }}
                      placeholder="Judul Badge (misal: 100% Terverifikasi)"
                      className="flex-1 px-3 py-1 text-xs bg-white border border-slate-300 rounded-lg font-bold"
                    />
                    <input
                      type="text"
                      value={badge.subtitle}
                      onChange={(e) => {
                        const newBadges = [...(product.trustBadges || [
                          { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                          { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' },
                        ])];
                        newBadges[idx].subtitle = e.target.value;
                        updateField('trustBadges', newBadges);
                      }}
                      placeholder="Subjudul (misal: Legalitas Berizin)"
                      className="flex-1 px-3 py-1 text-xs bg-white border border-slate-300 rounded-lg text-slate-600"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newBadges = (product.trustBadges || [
                          { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                          { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' },
                        ]).filter((_, i) => i !== idx);
                        updateField('trustBadges', newBadges);
                      }}
                      className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newBadges = [...(product.trustBadges || [
                      { title: '100% Terverifikasi', subtitle: 'Legalitas & EO Berizin Resmi' },
                      { title: 'Instruktur BNSP', subtitle: 'Pemandu Bersertifikasi' },
                    ]), { title: '', subtitle: '' }];
                    updateField('trustBadges', newBadges);
                  }}
                  className="px-3 py-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Tambah Point Jaminan</span>
                </button>
              </div>
            )}
          </div>

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Deskripsi Lengkap:</label>
              <input
                type="text"
                value={product.descriptionTitle || ''}
                onChange={(e) => updateField('descriptionTitle', e.target.value)}
                placeholder="Deskripsi Lengkap & Rincian Kegiatan"
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">Isi Narasi Deskripsi Lengkap:</label>
              <textarea
                rows={6}
                value={product.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Jelaskan secara mendetail susunan acara, latar belakang kegiatan, keunggulan lokasi, serta fasilitas yang didapatkan peserta..."
                className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl leading-relaxed"
              />
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: RUNDOWN / ITINERARY */}
      {activeTab === 'itinerary' && (
        <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-purple-600" />
              <span>Susunan Acara / Rundown Kegiatan (Itinerary)</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(product.showItinerary)}
                onChange={(e) => updateField('showItinerary', e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded-md"
              />
              <span className="text-xs font-bold text-slate-700">Tampilkan Rundown</span>
            </label>
          </div>

          {product.showItinerary && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Rundown:</label>
                <input
                  type="text"
                  value={product.itineraryTitle || ''}
                  onChange={(e) => updateField('itineraryTitle', e.target.value)}
                  placeholder="Rundown & Jadwal Kegiatan"
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                />
              </div>

              <div className="space-y-3 pt-2">
                {(product.itinerary || []).map((item, idx) => (
                  <div key={idx} className="p-3 bg-purple-50/50 rounded-xl border border-purple-200/80 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <input
                        type="text"
                        value={item.timeOrDay}
                        onChange={(e) => {
                          const newItin = [...(product.itinerary || [])];
                          newItin[idx].timeOrDay = e.target.value;
                          updateField('itinerary', newItin);
                        }}
                        placeholder="08.00 - 09.00 / Hari ke-1"
                        className="w-36 px-2.5 py-1 text-xs bg-white border border-slate-300 rounded-lg font-mono font-bold"
                      />
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => {
                          const newItin = [...(product.itinerary || [])];
                          newItin[idx].title = e.target.value;
                          updateField('itinerary', newItin);
                        }}
                        placeholder="Judul Agenda (misal: Registrasi & Ice Breaking)"
                        className="flex-1 px-3 py-1 text-xs bg-white border border-slate-300 rounded-lg font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newItin = (product.itinerary || []).filter((_, i) => i !== idx);
                          updateField('itinerary', newItin);
                        }}
                        className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <textarea
                      rows={2}
                      value={item.description || ''}
                      onChange={(e) => {
                        const newItin = [...(product.itinerary || [])];
                        newItin[idx].description = e.target.value;
                        updateField('itinerary', newItin);
                      }}
                      placeholder="Penjelasan detail singkat mengenai kegiatan ini..."
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    const newItin = [...(product.itinerary || []), { timeOrDay: '08.00 - 09.00', title: '', description: '' }];
                    updateField('itinerary', newItin);
                  }}
                  className="px-3 py-2 text-xs font-bold text-purple-700 bg-purple-50 rounded-xl hover:bg-purple-100 flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Tambah Agenda Rundown Baru</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 6: SYARAT & FAQ */}
      {activeTab === 'faqs' && (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-slate-600" />
                <span>Syarat & Ketentuan Pemesanan</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(product.showTerms)}
                  onChange={(e) => updateField('showTerms', e.target.checked)}
                  className="w-4 h-4 text-slate-700 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Seksi Syarat</span>
              </label>
            </div>

            {product.showTerms && (
              <>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Syarat & Ketentuan:</label>
                  <input
                    type="text"
                    value={product.termsTitle || ''}
                    onChange={(e) => updateField('termsTitle', e.target.value)}
                    placeholder="Syarat & Ketentuan Pemesanan"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Poin Syarat (1 per baris):</label>
                  <textarea
                    rows={4}
                    value={(product.terms || []).join('\n')}
                    onChange={(e) => updateField('terms', e.target.value.split('\n'))}
                    placeholder="Minimal pemesanan H-7 sebelum tanggal kegiatan&#10;DP minimal 30% saat konfirmasi penawaran&#10;Pelunasan dilakukan H-2 sebelum acara"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono"
                  />
                </div>
              </>
            )}
          </div>

          <div className="p-4 bg-white rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <HelpCircle className="w-4 h-4 text-blue-600" />
                <span>FAQ Khusus Produk / Layanan Ini</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(product.showFaqs)}
                  onChange={(e) => updateField('showFaqs', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Seksi FAQ</span>
              </label>
            </div>

            {product.showFaqs && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi FAQ:</label>
                  <input
                    type="text"
                    value={product.faqsTitle || ''}
                    onChange={(e) => updateField('faqsTitle', e.target.value)}
                    placeholder="Pertanyaan Sering Diajukan (FAQ)"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                </div>

                <div className="space-y-3 pt-1">
                  {(product.faqs || []).map((faq, idx) => (
                    <div key={idx} className="p-3 bg-blue-50/50 rounded-xl border border-blue-200/80 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => {
                            const newFaqs = [...(product.faqs || [])];
                            newFaqs[idx].question = e.target.value;
                            updateField('faqs', newFaqs);
                          }}
                          placeholder="Pertanyaan (misal: Apakah bisa custom lokasi acara?)"
                          className="flex-1 px-3 py-1 text-xs bg-white border border-slate-300 rounded-lg font-bold"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newFaqs = (product.faqs || []).filter((_, i) => i !== idx);
                            updateField('faqs', newFaqs);
                          }}
                          className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <textarea
                        rows={2}
                        value={faq.answer}
                        onChange={(e) => {
                          const newFaqs = [...(product.faqs || [])];
                          newFaqs[idx].answer = e.target.value;
                          updateField('faqs', newFaqs);
                        }}
                        placeholder="Jawaban penjelasan ramah..."
                        className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const newFaqs = [...(product.faqs || []), { question: '', answer: '' }];
                      updateField('faqs', newFaqs);
                    }}
                    className="px-3 py-2 text-xs font-bold text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Pertanyaan FAQ Baru</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 7: BOX PROPOSAL CORPORATE */}
      {activeTab === 'proposal' && (
        <div className="p-4 bg-slate-900 text-white rounded-2xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <label className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
              <Briefcase className="w-4 h-4" />
              <span>Box Penawaran Proposal Corporate / Group Inquiries</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={product.showCtaBox !== false}
                onChange={(e) => updateField('showCtaBox', e.target.checked)}
                className="w-4 h-4 text-amber-500 rounded-md"
              />
              <span className="text-xs font-bold text-slate-300">Tampilkan Box Proposal Ini</span>
            </label>
          </div>

          {product.showCtaBox !== false && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">Badge Tag Atas Box:</label>
                <input
                  type="text"
                  value={product.ctaBoxBadge || ''}
                  onChange={(e) => updateField('ctaBoxBadge', e.target.value)}
                  placeholder="Ingin Paket Khusus / Penawaran Resmi Corporate?"
                  className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-amber-300 font-bold"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">Judul Utama Box Proposal:</label>
                <input
                  type="text"
                  value={product.ctaBoxTitle || ''}
                  onChange={(e) => updateField('ctaBoxTitle', e.target.value)}
                  placeholder="Dapatkan Proposal & Surat Penawaran Resmi"
                  className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white font-bold"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1">Deskripsi Narasi Proposal:</label>
                <textarea
                  rows={3}
                  value={product.ctaBoxSubtitle || ''}
                  onChange={(e) => updateField('ctaBoxSubtitle', e.target.value)}
                  placeholder="Kami melayani penyusunan proposal resmi (RAB), penyesuaian jumlah peserta mulai dari 20 hingga 1.000+ orang, serta opsi survei lokasi gratis bersama tim EO."
                  className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-slate-200"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="text-xs font-bold text-slate-300 block mb-1">Teks Tombol CTA WA:</label>
                  <input
                    type="text"
                    value={product.ctaBoxButtonText || ''}
                    onChange={(e) => updateField('ctaBoxButtonText', e.target.value)}
                    placeholder="Minta Surat Penawaran & Proposal PDF via WA →"
                    className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-emerald-400 font-bold"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-300 block mb-1">Pesan Otomatis WA Khusus Proposal:</label>
                  <input
                    type="text"
                    value={product.ctaBoxWhatsappMessage || ''}
                    onChange={(e) => updateField('ctaBoxWhatsappMessage', e.target.value)}
                    placeholder="Halo Admin, mohon dibuatkan proposal resmi RAB untuk paket ini..."
                    className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-xl text-slate-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 8: ARTIKEL & PAKET TERKAIT */}
      {activeTab === 'related' && (
        <div className="space-y-4">
          <ProductRelatedArticlesManager
            allArticles={articles}
            selectedArticleIds={product.relatedArticleIds || []}
            onChangeArticleIds={(ids) => updateField('relatedArticleIds', ids)}
            customLinks={product.relatedArticleLinks || []}
            onChangeCustomLinks={(links) => updateField('relatedArticleLinks', links)}
            primaryColor={primaryColor}
          />

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-900">Seksi Rekomendasi Paket Layanan Lainnya (Bawah Halaman):</label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={product.showRelatedProducts !== false}
                  onChange={(e) => updateField('showRelatedProducts', e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded-md"
                />
                <span className="text-xs font-bold text-slate-700">Tampilkan Rekomendasi Paket Lain</span>
              </label>
            </div>

            {product.showRelatedProducts !== false && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Judul Seksi Rekomendasi:</label>
                  <input
                    type="text"
                    value={product.relatedProductsTitle || ''}
                    onChange={(e) => updateField('relatedProductsTitle', e.target.value)}
                    placeholder="Pilihan Paket Layanan Lainnya"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl font-bold"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Subjudul Seksi Rekomendasi:</label>
                  <input
                    type="text"
                    value={product.relatedProductsSubtitle || ''}
                    onChange={(e) => updateField('relatedProductsSubtitle', e.target.value)}
                    placeholder="Jelajahi paket alternatif yang mungkin sesuai dengan kebutuhan kegiatan Anda"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
